import _ from "lodash";
import { stringify } from "qs";

import toURLSearchParams from "../helpers/queryHelpers";
import type {
  FoodRecipeInspirationListApiArg,
  FoodSimpleRecipeListApiArg,
  FoodSimpleRecipeSuggestionsRetrieveApiArg,
  PaginatedUserList,
} from "./backendTypes";
import { backendApi } from "./backendTypes";

export enum TAGS {
  FAVOURITE = "FAVOURITE",
  DISLIKE = "DISLIKE",
  OWN_MEAL = "OWN_MEAL",
  USER = "USER",
  CLIENTS = "CLIENTS",
  CALENDAR_DAY = "CALENDAR_DAY",
  USER_MEASUREMENT_WEIGHT = "USER_MEASUREMENT_WEIGHT",
  USER_MEASUREMENT_SLEEP = "USER_MEASUREMENT_SLEEP",
  USER_MEASUREMENT_STRESS = "USER_MEASUREMENT_STRESS",
  USER_MEASUREMENT_WAIST_CIRCUMFERENCE = "USER_MEASUREMENT_WAIST_CIRCUMFERENCE",
  GENERATE_MEALS = "GENERATE_MEALS",
  RECIPE_INSPIRATION = "RECIPE_INSPIRATION",
  SIMPLE_RECIPE_INSPIRATION = "SIMPLE_RECIPE_INSPIRATION",
  RECIPES = "RECIPES",
  SIMPLE_GROCERY_LIST = "SIMPLE_GROCERY_LIST",
  RECIPE_MEAL = "RECIPE_MEAL",
}

// Spectacular does not recognise custom paginated response properties
export interface ExtendedPaginatedUserList extends PaginatedUserList {
  total_pages: number;
}

const LIKE_DISLIKE_TAGS = [TAGS.FAVOURITE, TAGS.DISLIKE];

// Helper function to convert object keys to snake_case
const keysToSnakeCase = (obj: Record<string, unknown>): Record<string, unknown> =>
  _.mapKeys(obj, (_value, key) => _.snakeCase(key));

export default backendApi.enhanceEndpoints({
  addTagTypes: Object.values(TAGS),

  endpoints: {
    foodRecipeInspirationList: {
      providesTags: [TAGS.RECIPE_INSPIRATION, TAGS.FAVOURITE],
      query: (queryArg: FoodRecipeInspirationListApiArg) => ({
        url: "/api/food/recipeInspiration/",
        params: toURLSearchParams(keysToSnakeCase(queryArg)),
      }),
    },
    foodGenerateMealsList: {
      providesTags: [TAGS.FAVOURITE, TAGS.OWN_MEAL, TAGS.GENERATE_MEALS],
    },
    usersClientsList: {
      providesTags: [TAGS.CLIENTS],
    },
    usersPaginatedclientsList: {
      providesTags: [TAGS.CLIENTS],
    },
    plannerCalendarDayList: {
      providesTags: [TAGS.CALENDAR_DAY],
    },
    usersUserfavouriterecipeList: {
      providesTags: [TAGS.FAVOURITE],
    },
    usersUserdislikerecipeList: {
      providesTags: [TAGS.DISLIKE],
    },

    plannerCalendarDayPartialUpdate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },

    usersUserfavouriterecipeCreate: {
      invalidatesTags: LIKE_DISLIKE_TAGS,
    },
    usersUserfavouriterecipeDestroy: {
      invalidatesTags: LIKE_DISLIKE_TAGS,
    },

    usersUserdislikerecipeCreate: {
      invalidatesTags: [TAGS.DISLIKE, TAGS.FAVOURITE],
    },
    usersUserdislikerecipeDestroy: {
      invalidatesTags: [TAGS.DISLIKE, TAGS.FAVOURITE],
    },

    // SimpleRecipe favorites/dislikes
    usersSimplerecipefavouriteList: {
      providesTags: [TAGS.FAVOURITE],
    },
    usersSimplerecipedislikeList: {
      providesTags: [TAGS.DISLIKE],
    },
    usersSimplerecipefavouriteCreate: {
      invalidatesTags: LIKE_DISLIKE_TAGS,
    },
    usersSimplerecipefavouriteDestroy: {
      invalidatesTags: LIKE_DISLIKE_TAGS,
    },
    usersSimplerecipedislikeCreate: {
      invalidatesTags: [TAGS.DISLIKE, TAGS.FAVOURITE],
    },
    usersSimplerecipedislikeDestroy: {
      invalidatesTags: [TAGS.DISLIKE, TAGS.FAVOURITE],
    },

    foodRecipeTemplateCreate: {
      invalidatesTags: [TAGS.OWN_MEAL],
    },
    foodRecipeTemplateDestroy: {
      invalidatesTags: [TAGS.OWN_MEAL],
    },

    usersAuthUsersCreate: {
      invalidatesTags: [TAGS.CLIENTS],
    },
    usersUserProfileCreate: {
      invalidatesTags: [TAGS.CLIENTS, TAGS.USER],
    },
    usersAuthUsersMeRetrieve: {
      providesTags: [TAGS.USER],
    },
    usersUserRetrieve: {
      providesTags: [TAGS.USER],
    },
    usersClientsRetrieve: {
      providesTags: [TAGS.CLIENTS],
    },
    usersOrganisationCreate: {
      invalidatesTags: [TAGS.USER],
    },
    usersOrganisationPartialUpdate: {
      invalidatesTags: [TAGS.USER],
    },
    usersClientsPartialUpdate: {
      invalidatesTags: [TAGS.CLIENTS],
    },
    usersUserProfileUpdate: {
      invalidatesTags: [
        TAGS.CLIENTS,
        TAGS.USER,
        TAGS.GENERATE_MEALS,
        TAGS.RECIPE_INSPIRATION,
        TAGS.SIMPLE_RECIPE_INSPIRATION,
      ],
    },
    usersUserProfilePartialUpdate: {
      invalidatesTags: [
        TAGS.CLIENTS,
        TAGS.USER,
        TAGS.GENERATE_MEALS,
        TAGS.RECIPE_INSPIRATION,
        TAGS.SIMPLE_RECIPE_INSPIRATION,
      ],
    },
    usersExerciseInstanceCreate: {
      invalidatesTags: [TAGS.CLIENTS],
    },
    usersExerciseInstanceUpdate: {
      invalidatesTags: [TAGS.CLIENTS],
    },
    usersExerciseInstancePartialUpdate: {
      invalidatesTags: [TAGS.CLIENTS],
    },
    usersExerciseInstanceDestroy: {
      invalidatesTags: [TAGS.CLIENTS],
    },
    usersWeeklyNutritionPlanCreate: {
      invalidatesTags: [TAGS.CLIENTS, TAGS.USER, TAGS.CALENDAR_DAY],
    },
    usersWeeklyNutritionPlanUpdate: {
      invalidatesTags: [TAGS.CLIENTS, TAGS.USER],
    },
    foodRecipeTemplatePartialUpdate: {
      invalidatesTags: [TAGS.OWN_MEAL],
    },
    plannerCopyMealsToMealMomentCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },
    plannerPlanGeneratedRecipeMealCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },
    plannerSaveAndPlanRecipeMealCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY, TAGS.OWN_MEAL],
    },
    plannerPlanSingleFoodMealCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },
    plannerPlanSimpleRecipeCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },

    // useMeasurementsUserWeightListQuery
    measurementsUserWeightList: {
      providesTags: [TAGS.USER_MEASUREMENT_WEIGHT],
    },
    // useMeasurementsUserSleepListQuery
    measurementsUserSleepList: {
      providesTags: [TAGS.USER_MEASUREMENT_SLEEP],
    },
    // useMeasurementsUserStressListQuery
    measurementsUserStressList: {
      providesTags: [TAGS.USER_MEASUREMENT_STRESS],
    },
    // useMeasurementsUserDistanceListQuery
    measurementsUserDistanceList: {
      providesTags: [TAGS.USER_MEASUREMENT_WAIST_CIRCUMFERENCE],
    },

    // useMeasurementsUserWeightCreateMutation
    measurementsUserWeightCreate: {
      invalidatesTags: [TAGS.USER_MEASUREMENT_WEIGHT],
    },
    // useMeasurementsUserSleepCreateMutation
    measurementsUserSleepCreate: {
      invalidatesTags: [TAGS.USER_MEASUREMENT_SLEEP],
    },
    // useMeasurementsUserStressCreateMutation
    measurementsUserStressCreate: {
      invalidatesTags: [TAGS.USER_MEASUREMENT_STRESS],
    },
    // useMeasurementsUserDistanceCreateMutation
    measurementsUserDistanceCreate: {
      invalidatesTags: [TAGS.USER_MEASUREMENT_WAIST_CIRCUMFERENCE],
    },

    measurementsDeleteMeasurementCreate: {
      invalidatesTags: [
        TAGS.USER_MEASUREMENT_SLEEP,
        TAGS.USER_MEASUREMENT_STRESS,
        TAGS.USER_MEASUREMENT_WAIST_CIRCUMFERENCE,
        TAGS.USER_MEASUREMENT_WEIGHT,
      ],
    },

    plannerAutoPlanDayCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },
    plannerAutoPlanDaysCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },
    plannerAutoplanSimpleRecipesCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },
    plannerCopyMealsFromDateCreate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },
    usersUserProfileCreateDefaultNutritionPlanCreate: {
      invalidatesTags: [TAGS.CLIENTS, TAGS.USER, TAGS.CALENDAR_DAY],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Patch the ME query cache with the new nutrition plan ID
          // The backend returns { success: true, message: string, weekly_nutrition_plan_id: number }
          if (data && typeof data === "object" && "weekly_nutrition_plan_id" in data) {
            const nutritionPlanId = (data as { weekly_nutrition_plan_id: number }).weekly_nutrition_plan_id;

            // Update the ME query cache
            dispatch(
              backendApi.util.updateQueryData("usersAuthUsersMeRetrieve", undefined, (draft) => {
                if (draft && draft.intake) {
                  // Set a placeholder object with just the ID since we don't have the full object yet
                  // The forceRefetch below will get the complete data
                  // eslint-disable-next-line no-param-reassign
                  draft.intake.weekly_nutrition_plan = {
                    id: nutritionPlanId,
                  } as unknown as typeof draft.intake.weekly_nutrition_plan;
                }
              })
            );

            // Also update the regular user query cache if it exists
            // This is important because simpleOnboardingSlice uses usersUserRetrieve
            // We'll try to get the current state to find the user ID
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
            const state = (dispatch as any).getState();
            const currentUserId = state?.user?.user?.id as number | undefined;

            if (currentUserId) {
              dispatch(
                backendApi.util.updateQueryData("usersUserRetrieve", { id: currentUserId }, (draft) => {
                  if (draft && draft.intake) {
                    // eslint-disable-next-line no-param-reassign
                    draft.intake.weekly_nutrition_plan = {
                      id: nutritionPlanId,
                    } as unknown as typeof draft.intake.weekly_nutrition_plan;
                  }
                })
              );
            }

            // Force refetch to ensure we have the complete updated data
            const refetchResult = dispatch(
              backendApi.endpoints.usersAuthUsersMeRetrieve.initiate(undefined, {
                forceRefetch: true,
                subscribe: false,
              })
            );

            // Clean up the subscription immediately
            refetchResult.unsubscribe();
          }
        } catch (error) {
          // If the mutation fails, we don't need to do anything
          // The invalidateTags will still run if the mutation succeeds
        }
      },
    },
    usersSetIngredientsToAvoidCreate: {
      invalidatesTags: [TAGS.USER],
    },

    // SimpleRecipe endpoints
    foodSimpleRecipeList: {
      providesTags: [TAGS.RECIPES, TAGS.FAVOURITE],
      query: (queryArg: FoodSimpleRecipeListApiArg) => ({
        url: "/api/food/simpleRecipe/",
        params: toURLSearchParams(queryArg as Record<string, unknown>),
      }),
    },
    foodSimpleRecipeSuggestionsRetrieve: {
      providesTags: [TAGS.RECIPES],
      query: (queryArg: FoodSimpleRecipeSuggestionsRetrieveApiArg) => ({
        url: "/api/food/simpleRecipe/suggestions/",
        params: toURLSearchParams(queryArg as Record<string, unknown>),
      }),
    },
    foodSimpleRecipeInspirationRetrieve: {
      providesTags: [TAGS.SIMPLE_RECIPE_INSPIRATION, TAGS.RECIPES],
      keepUnusedDataFor: 1800, // 30 minutes cache
    },
    foodSimpleRecipeMealPartialUpdate: {
      invalidatesTags: [TAGS.CALENDAR_DAY],
    },

    // Calendar item creation with potential SimpleRecipe
    plannerCalendarItemCreate: {
      invalidatesTags: (result, error, arg) => {
        const tags = [TAGS.CALENDAR_DAY];
        // If creating a calendar item with simple_recipe, invalidate recipes cache too
        if (arg.calendarItemRequest.content_type === "simplerecipemeal") {
          tags.push(TAGS.RECIPES);
        }
        return tags;
      },
    },

    // SimpleGroceryList endpoints
    plannerSimpleGroceryListList: {
      providesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },
    plannerSimpleGroceryListCreate: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },
    plannerSimpleGroceryListDestroy: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },
    plannerSimpleGroceryListUpdate: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },
    plannerSimpleGroceryListPartialUpdate: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },

    // SimpleGroceryListItem endpoints
    plannerSimpleGroceryListItemCreate: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },
    plannerSimpleGroceryListItemDestroy: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },
    plannerSimpleGroceryListItemUpdate: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },
    plannerSimpleGroceryListItemPartialUpdate: {
      invalidatesTags: [TAGS.SIMPLE_GROCERY_LIST],
    },

    // RecipeMeal endpoints
    foodRecipeMealRetrieve: {
      providesTags: [TAGS.RECIPE_MEAL],
    },
    foodRecipeMealPartialUpdate: {
      invalidatesTags: [TAGS.RECIPE_MEAL, TAGS.CALENDAR_DAY],
    },

    // Ingredient endpoints
    foodIngredientCreate: {
      invalidatesTags: [TAGS.RECIPE_MEAL, TAGS.CALENDAR_DAY],
    },
    foodIngredientPartialUpdate: {
      invalidatesTags: [TAGS.RECIPE_MEAL, TAGS.CALENDAR_DAY],
    },
    foodIngredientDestroy: {
      invalidatesTags: [TAGS.RECIPE_MEAL, TAGS.CALENDAR_DAY],
    },
  },
});
